/**
 * The robots directive for a business profile, and the one place it is decided.
 *
 * Extracted from the profile route so the decision is assertable. The sitemap
 * filters here and the page emits its directive here, and nothing was holding
 * those two together except that both happened to call the same predicate —
 * a property no test could observe and a refactor could quietly break.
 *
 * The invariant, asserted in profile-indexing.test.ts over every state a
 * profile can be in:
 *
 *     profileInSitemap(b) === (profileRobots(b) === undefined)
 *
 * It holds for the editorial floor AND for an editor's manual noindex, which
 * is why both take the same input. A sitemap should advertise canonical,
 * indexable URLs only: listing one that serves `noindex` earns a standing
 * "Submitted URL marked noindex" in Search Console and keeps drawing recrawls
 * to a page we have already said not to keep. On a site working its way back
 * from a demotion, manufacturing coverage errors is the last thing worth doing
 * to save a join.
 */

import { clearsProfileFloor } from '#app/scoring/scoring-model.ts'

export interface ProfileIndexingInput {
	reviewsHeld: number | null
	synthesisAt: Date | string | null
	pros: string[]
	watchOuts: string[]
	/** An editor's manual pull, from SeoMeta.noIndex. Absent means false. */
	seoNoIndex?: boolean | null
}

/**
 * Two different noindex decisions, and they resolve differently.
 *
 * `seoNoIndex` is an editor's manual call on one page, so it keeps `nofollow` —
 * a page pulled by hand is one we do not want crawled onward either.
 *
 * The floor is structural: the profile carries no editorial that survived
 * grounding, so it has nothing to say. Those pages stay `follow`, because they
 * still carry real links into the category and city rankings and there is no
 * reason to strand that. They also stay live and claimable — this removes them
 * from the index, not from the directory, and an owner claiming one is still
 * the point.
 *
 * Returns undefined for an indexable page rather than 'index, follow', because
 * the default is the absence of the tag and emitting one says nothing.
 */
export function profileRobots(business: ProfileIndexingInput): string | undefined {
	if (business.seoNoIndex) return 'noindex, nofollow'
	return clearsProfileFloor(business) ? undefined : 'noindex, follow'
}

/** Whether the sitemap may advertise this profile. */
export function profileInSitemap(business: ProfileIndexingInput): boolean {
	return profileRobots(business) === undefined
}
